import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  CircularProgress,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
  IconButton,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { ContentCopy, Refresh } from '@mui/icons-material';
import { apiClient } from '../../services/apiClient';
import { extractErrorMessage } from '../../utils/errorUtils';
import { useUserRoles } from '../../hooks/useUserRoles';

interface UserCreateData {
  name: string;
  email: string;
  password: string;
  role: string;
  api_access_enabled: boolean;
}

interface UserCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onUserCreated: () => void;
}

const ALL_ROLES = [
  { value: 'USER', label: 'User' },
  { value: 'ORG_ADMIN', label: 'Organisation Admin' },
  { value: 'SUPER_ADMIN', label: 'Super Admin' }
];

// Generate a strong password (meets 3 of 4 category requirement)
const generatePassword = (): string => {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const symbols = '!@#$%^&*';
  const allChars = uppercase + lowercase + numbers + symbols;
  
  // Ensure at least 3 categories (we'll include all 4 for stronger passwords)
  let password = '';
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += symbols[Math.floor(Math.random() * symbols.length)];
  
  // Fill the rest randomly (total length: 12)
  for (let i = 4; i < 12; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  
  // Shuffle the password to avoid predictable patterns
  return password.split('').sort(() => 0.5 - Math.random()).join('');
};

export default function UserCreateDialog({ open, onClose, onUserCreated }: UserCreateDialogProps) {
  const { isSuperAdmin } = useUserRoles();
  const availableRoles = isSuperAdmin()
    ? ALL_ROLES
    : ALL_ROLES.filter(r => r.value !== 'SUPER_ADMIN');

  const [formData, setFormData] = useState<UserCreateData>({
    name: '',
    email: '',
    password: '',
    role: 'USER',
    api_access_enabled: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate a new password when dialog opens
  useEffect(() => {
    if (open && !formData.password) {
      setFormData(prev => ({
        ...prev,
        password: generatePassword()
      }));
    }
  }, [open]);

  const handleClose = () => {
    if (!loading) {
      setFormData({ name: '', email: '', password: '', role: 'USER', api_access_enabled: false });
      setError(null);
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.password.trim()) {
      setError('Name, email, and password are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await apiClient.post('/admin/users', formData);
      
      // Success - close dialog and refresh users list
      handleClose();
      
      // Safely call onUserCreated without letting errors bubble up
      try {
        onUserCreated();
      } catch (refreshError) {
        console.error('Failed to refresh users list:', refreshError);
        // Don't show error to user for refresh failure
      }
    } catch (err: any) {
      console.error('User creation error:', err);
      setError(extractErrorMessage(err, 'Failed to create user'));
      
      // Prevent the error from bubbling up to error boundary
      // by not re-throwing it
    } finally {
      setLoading(false);
    }
  };

  const handleTextInputChange = (field: keyof UserCreateData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSelectChange = (field: keyof UserCreateData) => (
    event: SelectChangeEvent<string>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSwitchChange = (field: keyof UserCreateData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(formData.password);
      // Could add a toast notification here
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  const handleRegeneratePassword = () => {
    setFormData(prev => ({
      ...prev,
      password: generatePassword()
    }));
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit
      }}
    >
      <DialogTitle>Create New User</DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Full Name"
            value={formData.name}
            onChange={handleTextInputChange('name')}
            required
            disabled={loading}
            fullWidth
          />

          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleTextInputChange('email')}
            required
            disabled={loading}
            fullWidth
          />

          <TextField
            label="Password"
            type="text"
            value={formData.password}
            onChange={handleTextInputChange('password')}
            required
            disabled={loading}
            fullWidth
            helperText="Password must be at least 8 characters with 3 of: uppercase, lowercase, numbers, or special characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy password">
                    <IconButton
                      onClick={handleCopyPassword}
                      disabled={loading}
                      size="small"
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Generate new password">
                    <IconButton
                      onClick={handleRegeneratePassword}
                      disabled={loading}
                      size="small"
                    >
                      <Refresh fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />

          <FormControl fullWidth required disabled={loading}>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              onChange={handleSelectChange('role')}
              label="Role"
            >
              {availableRoles.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isSuperAdmin() && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.api_access_enabled}
                  onChange={handleSwitchChange('api_access_enabled')}
                  disabled={loading}
                />
              }
              label="API Access Enabled"
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          variant="contained" 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          Create User
        </Button>
      </DialogActions>
    </Dialog>
  );
}